import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, Landmark, Save, Download, Send, FileText } from "lucide-react";
import axios from "axios";
import QRCode from "qrcode";
import { FIRM } from "@/lib/firm";
import { sha256 } from "@/lib/sha256";
import { numberToWords } from "@/lib/format";
import { dash, money, fmtDate, borrowerSigOf } from "@/pages/CollateralMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);

export const TC_FORMS = [
  { key: "", label: "Full Package (Agreement + IRS forms)", short: "Full Package" },
  { key: "instructions", label: "Client Guide — How Your Program Works", short: "Client Guide" },
  { key: "agreement", label: "Master Solar Tax Credit Transfer Agreement", short: "Agreement" },
  { key: "election", label: "§6418 Transfer Election Statement", short: "Election Stmt" },
  { key: "certificate", label: "Solar Tax Credit Transfer Certificate (check-size)", short: "Certificate" },
  { key: "retainer", label: "Retainer & Representation Agreement", short: "Retainer" },
  { key: "poa", label: "Limited Power of Attorney — Attorney-in-Fact", short: "POA" },
  { key: "f2848", label: "IRS Form 2848 — Power of Attorney (official)", short: "2848" },
  { key: "f8821", label: "IRS Form 8821 — Tax Information Authorization (official)", short: "8821" },
  { key: "f12277", label: "IRS Form 12277 — Withdrawal of Filed NFTL (official)", short: "12277" },
  { key: "f911", label: "IRS Form 911 — Taxpayer Advocate / Hardship (official)", short: "911" },
  { key: "f9465", label: "IRS Form 9465 — Installment Agreement Request (official)", short: "9465" },
  { key: "f433f", label: "IRS Form 433-F — Collection Information Statement (official)", short: "433-F" },
  { key: "f843", label: "IRS Form 843 — Claim for Refund & Abatement (official)", short: "843" },
  { key: "f656", label: "IRS Form 656 — Offer in Compromise (official)", short: "656" },
  { key: "f1040v", label: "IRS Form 1040-V — Payment Voucher (official)", short: "1040-V" },
];
export const TC_IRS_CODES = ["f2848", "f8821", "f12277", "f911", "f9465", "f433f", "f843", "f656", "f1040v"];

export function feeForDebt(debt) {
  const d = Math.max(0, Number(debt) || 0);
  if (d <= 10000) return 850;
  return 850 + Math.ceil((d - 10000) / 1000) * 25;
}

const CursiveSig = ({ name }) => name
  ? <span className="tc-sig-cursive" style={{ fontSize: "20pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

export const TC_CSS = `
  .tc-sheet{background:#fff;color:#14140F;max-width:8.5in;margin:0 auto 28px;padding:0.55in 0.6in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:10.5pt;line-height:1.4;}
  .tc-head{text-align:center;border-bottom:2.5px solid #0B3D2E;padding-bottom:9px;margin-bottom:12px;}
  .tc-eyebrow{font-size:8pt;letter-spacing:.16em;color:#0B3D2E;font-weight:700;text-transform:uppercase;}
  .tc-title{font-size:16pt;font-weight:700;margin:5px 0 3px;text-transform:uppercase;letter-spacing:.02em;}
  .tc-sub{font-size:8.5pt;color:#555;font-style:italic;}
  .tc-sig-cursive{font-family:'Great Vibes','Snell Roundhand',cursive;color:#1b2a52;}
  .tc-form{page-break-before:always;page-break-inside:avoid;break-inside:avoid;}
  .tc-solo .tc-form{page-break-before:auto!important;break-before:auto!important;}
  .tc-cert-solo{padding:0.3in 0.35in!important;max-width:8.5in;}
  .tc-formhdr{display:flex;justify-content:space-between;align-items:flex-end;border:2px solid #0B3D2E;padding:5px 8px;margin:0 0 7px;background:#eaf3ee;}
  .tc-fno{font-size:8.5pt;font-weight:700;color:#0B3D2E;letter-spacing:.05em;}
  .tc-fname{font-size:12.5pt;font-weight:700;text-transform:uppercase;}
  .tc-frev{font-size:7pt;color:#666;text-align:right;max-width:48%;}
  .tc-art{margin:12px 0 2px;font-size:11pt;font-weight:700;color:#0B3D2E;text-transform:uppercase;letter-spacing:.03em;border-bottom:1px solid #cfe0d7;padding-bottom:2px;page-break-after:avoid;}
  .tc-sheet p{text-align:justify;margin:5px 0;}
  .tc-f{display:flex;gap:8px;padding:2px 6px;border:1px solid #d7e0d9;border-top:none;}
  .tc-f:first-of-type{border-top:1px solid #d7e0d9;}
  .tc-fl{flex:0 0 40%;font-weight:600;color:#333;font-size:9pt;text-transform:uppercase;letter-spacing:.02em;}
  .tc-fv{flex:1;font-size:9.5pt;}
  .tc-blank{display:inline-block;min-width:150px;border-bottom:1px solid #333;}
  .tc-note{font-size:7.8pt;color:#6b6b63;margin-top:4px;font-style:italic;}
  .tc-grid2{display:flex;gap:14px;} .tc-grid2>div{flex:1;}
  .tc-sigrow{display:flex;gap:22px;margin-top:8px;} .tc-sigrow>div{flex:1;}
  .tc-sigrow p{font-size:9.5pt;margin:2px 0;}
  .tc-hash{font-family:'Courier New',monospace;font-size:8.5pt;word-break:break-all;color:#14204a;background:#eef3f0;padding:1px 4px;border-radius:3px;}
  .tc-chk{font-weight:700;}
  .tc-callout{border:1px solid #cfe0d7;background:#f3f9f5;border-radius:5px;padding:8px 10px;margin-top:8px;font-size:8.6pt;color:#1a3a2d;}

  @media print{
    body{background:#fff!important;}
    .tc-workbench{background:#fff!important;min-height:0!important;}
    .no-print{display:none!important;}
    .cm-print-area{padding:0!important;max-width:none!important;}
    .tc-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.3in 0.4in!important;}
    .tc-art,.tc-title,.tc-fname{break-after:avoid;}
    .tc-cert-solo{padding:0.25in 0.3in!important;max-width:none!important;}
    @page{size:letter;margin:0.35in;}
  }
`;

const B = () => <span className="tc-blank" />;
const F = ({ label, children }) => (
  <div className="tc-f"><span className="tc-fl">{label}</span><span className="tc-fv">{children || <B />}</span></div>
);
const val = (shared, key, auto) => (shared[key] !== undefined && shared[key] !== "" ? shared[key] : (auto || ""));

export const tcDefaults = () => ({
  date: todayISO(), agreementNo: "", certNo: "", registrationNo: "", taxDebt: "", taxYears: "",
  tin: "", creditType: "IRC §48 Investment Tax Credit — solar energy property", creditYear: "",
  lienSerial: "", noticeFileRef: "", irsOffice: "IRS — Centralized Lien Operation, P.O. Box 145595, Cincinnati, OH 45250-5595",
  state: FIRM.notaryState,
  employerName: "", employerAddress: "", employerContact: "",
  bankName: "", bankAddress: "", bankAccount: "", levyType: "wage",
  assigneeName: "", assigneeTin: "", assigneeAddress: "",
});

function amountWords(n) {
  const v = Math.round(Number(n) || 0);
  if (!v) return "";
  try { return numberToWords(v); } catch { return ""; }
}

function maskTin(t) {
  const s = String(t || "").replace(/[^0-9A-Za-z]/g, "");
  if (!s) return "";
  if (s.length < 4) return "***-**-****";
  return `***-**-${s.slice(-4)}`;
}

export function TaxCreditTransferDoc({ app, shared, last, formKey }) {
  const binder = app.binder || {};
  const borrowerName = app.borrower_full_name || app.borrower_name || "";
  const borrowerAddr = app.borrower_address || "";
  const tinAuto = app.borrower_ssn || app.tin || "";
  const debtAuto = app.tax_debt || app.payoff_amount || app.current_balance || binder.payoffAmount || "";
  const auto = { taxDebt: debtAuto, tin: tinAuto };
  const g = (k) => val(shared, k, auto[k]);

  const ref6 = String(parseInt(sha256(String(app.id || borrowerName || "seed")).slice(0, 8), 16)).slice(0, 6).padEnd(6, "0");
  const yr = (g("date") || todayISO()).slice(0, 4);
  const agreementNo = g("agreementNo") || `STF-TCT-${yr}-${ref6}`;
  const registrationNo = g("registrationNo") || `IRS-REG-${yr}-${ref6}`;
  const creditNo = g("certNo") || `STF-CEC-${yr}-${ref6}`;
  const creditYear = g("creditYear") || yr;
  const creditType = g("creditType") || "IRC §48 Investment Tax Credit — solar energy property";
  const debt = Number(g("taxDebt")) || 0;
  const fee = feeForDebt(debt);
  const bSig = borrowerSigOf(app);
  const firmSig = `/s/ ${FIRM.officer}`;
  const stampTime = `${(g("date") || todayISO())} · ${new Date().toISOString().slice(11, 19)} UTC`;
  const execHash = sha256([FIRM.name, agreementNo, registrationNo, borrowerName, debt, g("tin"), g("date")].map((x) => String(x || "")).join("|"));
  const taxYears = g("taxYears") || "the eligible taxable year";

  // QR code → public certificate verification page (shown on the record stub).
  const verifyUrl = (typeof window !== "undefined" ? window.location.origin : "") + `/verify-certificate/${app.id}`;
  const [certQr, setCertQr] = useState("");
  useEffect(() => {
    if (formKey !== "certificate") return;
    let alive = true;
    QRCode.toDataURL(verifyUrl, { margin: 1, width: 240, errorCorrectionLevel: "M" })
      .then((d) => { if (alive) setCertQr(d); }).catch(() => {});
    return () => { alive = false; };
  }, [verifyUrl, formKey]);

  // §6418 Transfer Election Statement — the statement the transferee attaches
  // to its federal income tax return (Treas. Reg. §1.6418-2(b)) to claim the
  // transferred eligible credit. This is NOT a bank instrument and is NOT
  // filed with a lien/levy unit.
  const ElectionStatement = ({ heading }) => (
    <div data-testid={`tc-election-${app.id}`}>
      {heading && (
        <div className="tc-art" style={{ marginTop: 10 }}>Transfer Election Statement — IRC §6418</div>
      )}
      <div className="tc-callout" style={{ marginBottom: 8 }}>
        Attach this statement to the <strong>transferee's federal income tax return</strong> (with Form 3800, General Business Credit)
        for the eligible taxable year to claim the transferred credit. The transfer is valid only if a valid IRS pre-filing
        <strong> registration number</strong> is included. This statement is not a bank check or draft and is not filed with an IRS lien or levy unit.
      </div>
      <F label="Transferor (Eligible Taxpayer / Seller)">{FIRM.name} · EIN {FIRM.ein}</F>
      <F label="Transferee (Buyer / Taxpayer)">{dash(borrowerName)} · TIN {dash(g("tin"))}</F>
      <F label="Master Agreement No. (credit drawn from)">{agreementNo}</F>
      <F label="Eligible Credit Transferred">{creditType}</F>
      <F label="Credit (Eligible) Taxable Year">{creditYear}</F>
      <F label="Amount of Credit Transferred">{debt ? money(debt) : <B />}{debt ? ` (${amountWords(debt)} dollars)` : ""}</F>
      <F label="Cash Consideration Paid by Transferee">{money(fee)}</F>
      <F label="IRS Pre-Filing Registration Number">{registrationNo}</F>
      <F label="Credit Certificate No.">{creditNo}</F>
      <p style={{ marginTop: 6 }}>
        This credit is <strong>drawn from and transferred under the SEUTRADE Master Solar Tax Credit Transfer Agreement No. {agreementNo}</strong>.
        Pursuant to <strong>IRC §6418</strong> and the regulations thereunder, the Transferor and Transferee elect to transfer the specified
        eligible credit for cash consideration. The parties certify that: (a) the credit is an eligible credit determined with respect to the
        Transferor's eligible credit property; (b) no portion of the credit has been or will be transferred to any other person; (c) the
        consideration is paid in cash and is not included in the Transferor's gross income nor deductible by the Transferee; and (d) the
        Transferor has provided the minimum required documentation substantiating the credit.
      </p>
      <div className="tc-sigrow">
        <div>
          <p style={{ fontWeight: 700 }}>Transferor</p>
          <p>Signature: <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
        <div>
          <p style={{ fontWeight: 700 }}>Transferee / Taxpayer</p>
          <p>Signature: <CursiveSig name={bSig} /></p>
          <p>Name: {dash(borrowerName)} · Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
      </div>
      <p className="tc-note">The transferred credit offsets the transferee's federal income tax liability on a filed return, subject to the general business credit ordering, passive activity and at-risk limitations, and possible recapture under §50. It is not guaranteed by the United States and is subject to IRS examination.</p>
    </div>
  );

  // Representation / Attorney-in-Fact documents --------------------------------
  const retainer = (
    <div className="tc-form" data-testid={`tc-retainer-${app.id}`}>
      <div className="tc-head" style={{ marginBottom: 10 }}>
        <div className="tc-eyebrow">{FIRM.name} · Engagement</div>
        <div className="tc-title" style={{ fontSize: "15pt" }}>Retainer &amp; Representation Agreement</div>
        <div className="tc-sub">Engagement of {FIRM.name} as the Client's authorized representative &amp; attorney-in-fact for the Tax Debt Assistance Program</div>
      </div>
      <F label="Agreement No. / Date">{agreementNo} · {g("date") ? fmtDate(g("date")) : <B />}</F>
      <F label="Client / Taxpayer">{dash(borrowerName)}</F>
      <F label="Client Address / TIN">{[dash(borrowerAddr), `TIN ${dash(g("tin"))}`].join(" · ")}</F>
      <F label="Representative / Firm">{FIRM.name} · EIN {FIRM.ein} · {FIRM.address}</F>
      <F label="Program / Service Fee">{money(fee)} + $9.99/mo</F>
      <div className="tc-art">1. Engagement &amp; Scope</div>
      <p>The Client retains {FIRM.name} (the "Firm") to provide tax-debt resolution services under the SEUTRADE Tax Debt Assistance Program, including: brokering and documenting the transfer of an eligible clean-energy (solar) tax credit under IRC §6418; and preparing the Client's IRS request submissions as applicable — Forms 2848, 8821, 12277, 911, 9465, 433-F, 843, 656 and the 1040-V payment voucher — and corresponding with the Internal Revenue Service on the Client's behalf.</p>
      <div className="tc-art">2. Authorization as Representative &amp; Attorney-in-Fact</div>
      <p>The Client authorizes the Firm and its authorized officer to act as the Client's representative and attorney-in-fact for the limited purposes of this engagement, as further set out in the accompanying Limited Power of Attorney and IRS Forms 2848 and 8821. This authorization is non-exclusive and limited to the tax periods and matters stated below.</p>
      <F label="Tax Matters / Form(s)">U.S. Individual Income Tax — Form 1040 and related assessments, liens, levies and penalties</F>
      <F label="Tax Period(s)">{taxYears}</F>
      <div className="tc-art">3. Fee &amp; Payment</div>
      <p>The Client shall pay the Firm a program fee of <strong>{money(fee)}</strong>, computed as $850 for the first $10,000 of IRS balance plus $25 for each additional $1,000 (rounded up to the next $1,000), plus a recurring <strong>$9.99 monthly service fee</strong> charged on the 1st of each month to the card on file until cancelled. Payment is made through the Program's secure Stripe checkout.</p>
      <div className="tc-art">4. Client Responsibilities &amp; No Guarantee</div>
      <p>The Client represents that the information provided (identity, TIN, balances and periods) is accurate. The Firm's services do not guarantee any particular IRS outcome; relief, releases, abatements and the use of any transferred credit depend on IRS review and acceptance, the Client's own tax liability, and applicable law. "Attorney-in-fact" means an agent appointed under a power of attorney and does not imply the Firm is a licensed attorney-at-law or CPA.</p>
      <div className="tc-art">5. Term &amp; Governing Law</div>
      <F label="Governing State">{g("state")}</F>
      <div className="tc-sigrow">
        <div>
          <p style={{ fontWeight: 700 }}>Client / Taxpayer</p>
          <p>Signature: <CursiveSig name={bSig} /></p>
          <p>Name: {dash(borrowerName)} · Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
        <div>
          <p style={{ fontWeight: 700 }}>{FIRM.name} (Firm)</p>
          <p>Signature: <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
      </div>
      <p>Execution Hash (SHA-256): <span className="tc-hash">{execHash}</span></p>
    </div>
  );

  const poa = (
    <div className="tc-form" data-testid={`tc-poa-${app.id}`}>
      <div className="tc-head" style={{ marginBottom: 10 }}>
        <div className="tc-eyebrow">{FIRM.name} · Appointment</div>
        <div className="tc-title" style={{ fontSize: "15pt" }}>Limited Power of Attorney — Appointment of Attorney-in-Fact</div>
        <div className="tc-sub">Private, limited &amp; durable — for the Tax Debt Assistance Program and IRS filings only</div>
      </div>
      <F label="Principal (Client / Taxpayer)">{dash(borrowerName)}</F>
      <F label="Principal Address / TIN">{[dash(borrowerAddr), `TIN ${dash(g("tin"))}`].join(" · ")}</F>
      <F label="Attorney-in-Fact (Agent)">{FIRM.officer}, for and on behalf of {FIRM.name} · EIN {FIRM.ein}</F>
      <p>The Principal hereby appoints the above Attorney-in-Fact, with full power of substitution limited to the Program, to do the following on the Principal's behalf with respect to U.S. Individual Income Tax (Form 1040) for the tax period(s) <strong>{taxYears}</strong>:</p>
      <p>(a) prepare, sign, assemble, submit and mail the Client's IRS request forms (Forms 2848, 8821, 12277, 911, 9465, 433-F, 843, 656 and the 1040-V payment voucher) and the clean-energy tax-credit transfer documents; (b) request withdrawal of a filed Notice of Federal Tax Lien, release of levy/garnishment, installment agreement, offer in compromise and/or abatement where eligible; (c) receive, inspect and respond to related IRS correspondence; and (d) take such further acts as are reasonably necessary to carry out the foregoing.</p>
      <p>This Limited Power of Attorney is durable, shall not be affected by the Principal's subsequent disability, and remains in effect until the stated matters are concluded or until revoked in writing. "Attorney-in-fact" denotes an agent under this power and not a licensed attorney-at-law. This is a private instrument, not issued or guaranteed by any government agency.</p>
      <div className="tc-sigrow">
        <div>
          <p style={{ fontWeight: 700 }}>Principal / Taxpayer</p>
          <p>Signature: <CursiveSig name={bSig} /></p>
          <p>Name: {dash(borrowerName)} · Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
        <div>
          <p style={{ fontWeight: 700 }}>Attorney-in-Fact (accepts appointment)</p>
          <p>Signature: <CursiveSig name={firmSig} /> — {FIRM.officer}, for {FIRM.name}</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
      </div>
      <p style={{ fontSize: "9pt" }}>Acknowledged before me this ______ day of __________, 20____ — Notary Public, State of {dash(g("state"))}. [Notary Seal]</p>
    </div>
  );

  const blocks = { retainer, poa };

  if (formKey === "certificate") {
    const cardBorder = "2px solid #0B3D2E";
    return (
      <div className="tc-sheet tc-solo tc-cert-solo" data-testid={`tc-solo-certificate-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <div data-testid={`tc-cert-${app.id}`} style={{ width: "100%", margin: "0 auto", fontFamily: "Georgia,'Times New Roman',serif", color: "#0B3D2E" }}>
          {/* CHECK FACE — full page width, 3.5in tall (standard business-check height) */}
          <div style={{ position: "relative", height: "3.5in", border: cardBorder, borderRadius: 10, background: "linear-gradient(135deg,#f3f9f5 0%,#eaf3ee 60%,#f6efd9 100%)", padding: "0.24in 0.3in", overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", transform: "rotate(-15deg)", fontSize: "34pt", fontWeight: 800, color: "rgba(11,61,46,0.07)", letterSpacing: ".12em", pointerEvents: "none", whiteSpace: "nowrap" }}>NON‑NEGOTIABLE · NOT A CHECK</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
              <div>
                <div style={{ fontSize: "12.5pt", fontWeight: 700, letterSpacing: ".02em" }}>{FIRM.name}</div>
                <div style={{ fontSize: "7.5pt", color: "#3c5a4c" }}>{FIRM.address} · EIN {FIRM.ein}</div>
                <div style={{ fontSize: "9.5pt", fontWeight: 700, color: "#B8860B", marginTop: 3, textTransform: "uppercase", letterSpacing: ".06em" }}>Solar Tax Credit Transfer Certificate</div>
                <div style={{ fontSize: "7.5pt", color: "#3c5a4c" }}>Issued under IRC §6418 · §48 Solar Investment Tax Credit</div>
              </div>
              <div style={{ textAlign: "right", fontSize: "8.5pt" }}>
                <div><b>Certificate No.</b> {creditNo}</div>
                <div><b>Date</b> {g("date") ? fmtDate(g("date")) : "____________"}</div>
                <div><b>Credit Year</b> {creditYear}</div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "0.22in", position: "relative" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "7.5pt", textTransform: "uppercase", letterSpacing: ".08em", color: "#3c5a4c" }}>Credit payable to — Transferee</div>
                <div style={{ fontSize: "15pt", fontWeight: 700, borderBottom: "1px solid #0B3D2E", paddingBottom: 2, marginTop: 2, minHeight: "0.3in" }}>{dash(borrowerName)}</div>
                <div style={{ fontSize: "7.5pt", color: "#3c5a4c", marginTop: 2 }}>TIN {g("tin") ? maskTin(g("tin")) : "—"} · last 4 shown for security</div>
              </div>
              <div style={{ marginLeft: "0.3in", textAlign: "right" }}>
                <div style={{ fontSize: "7.5pt", textTransform: "uppercase", letterSpacing: ".08em", color: "#3c5a4c" }}>Credit amount</div>
                <div style={{ border: "2px solid #0B3D2E", borderRadius: 6, padding: "4px 12px", fontSize: "17pt", fontWeight: 800, fontFamily: "'Courier New',monospace", background: "#fff" }}>{debt ? money(debt) : "$ ____________"}</div>
              </div>
            </div>
            <div style={{ fontSize: "9pt", fontStyle: "italic", borderBottom: "1px solid #9ab3a7", paddingBottom: 2, marginTop: 6, position: "relative" }}>
              {debt ? `${amountWords(debt)} dollars` : "______________________________________________"} — in solar tax credit
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "0.16in", position: "relative" }}>
              <div style={{ fontSize: "7.5pt", color: "#3c5a4c", lineHeight: 1.4 }}>
                <div><b>IRS Registration No.:</b> {registrationNo}</div>
                <div><b>Master Agreement No.:</b> {agreementNo}</div>
                <div style={{ fontSize: "6pt", color: "#5a7568", marginTop: 1 }}><b>E‑Sign Hash (SHA‑256):</b> <span style={{ fontFamily: "'Courier New',monospace", wordBreak: "break-all" }}>{execHash}</span></div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Great Vibes','Snell Roundhand',cursive", fontSize: "19pt", color: "#1b2a52", lineHeight: 1, borderBottom: "1px solid #0B3D2E", padding: "0 18px 2px" }}>{firmSig}</div>
                <div style={{ fontSize: "7pt", color: "#3c5a4c", marginTop: 2 }}>{FIRM.officer} · Authorized Officer</div>
              </div>
            </div>
            <div style={{ position: "absolute", left: "0.3in", right: "0.3in", bottom: "0.12in", fontSize: "6.4pt", color: "#5a7568", textAlign: "center", lineHeight: 1.35 }}>
              This certificate evidences a tax‑credit transfer under IRC §6418 and the SEUTRADE Master Solar Tax Credit Transfer Agreement. It is <b>NOT</b> a check, draft, money order, or negotiable instrument, is <b>NOT</b> drawn on any bank or the United States Treasury, has no cash or face value, and may not be deposited or cashed. The credit is claimed only on the transferee's federal income tax return (Form 3800).
            </div>
          </div>

          {/* PERFORATION */}
          <div style={{ borderTop: "2px dashed #8aa99b", margin: "2px 0", position: "relative" }}>
            <span style={{ position: "absolute", right: 8, top: -7, fontSize: "6.5pt", color: "#8aa99b", background: "#fff", padding: "0 4px" }}>✂ detach &amp; retain stub</span>
          </div>

          {/* STUB */}
          <div style={{ border: "1px solid #cfe0d7", borderRadius: 6, padding: "10px 16px", background: "#fff", fontSize: "8.5pt", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 18px" }}>
            <div style={{ gridColumn: "1 / span 3", fontWeight: 700, color: "#0B3D2E", textTransform: "uppercase", letterSpacing: ".06em", fontSize: "8pt" }}>Record Stub — Solar Tax Credit Transfer</div>
            <div><b>Certificate</b><br />{creditNo}</div>
            <div><b>Transferee</b><br />{dash(borrowerName)}</div>
            <div><b>Transferee TIN</b><br />{g("tin") ? maskTin(g("tin")) : "—"}</div>
            <div><b>Credit Amount</b><br />{debt ? money(debt) : "$ ________"}</div>
            <div><b>Credit Year</b><br />{creditYear}</div>
            <div><b>IRS Registration No.</b><br />{registrationNo}</div>
            <div><b>Master Agreement</b><br />{agreementNo}</div>
            <div style={{ gridColumn: "1 / span 3", fontSize: "6.8pt", color: "#5a7568", wordBreak: "break-all" }}><b>E‑Sign Hash (SHA‑256):</b> <span style={{ fontFamily: "'Courier New',monospace" }}>{execHash}</span></div>
            <div style={{ gridColumn: "1 / span 3", display: "flex", alignItems: "center", gap: 12, marginTop: 2 }}>
              {certQr && <img src={certQr} alt="Scan to verify" data-testid={`tc-cert-qr-${app.id}`} style={{ width: "0.62in", height: "0.62in", flex: "0 0 auto", border: "1px solid #cfe0d7", borderRadius: 4, background: "#fff" }} />}
              <div style={{ fontSize: "6.8pt", color: "#5a7568" }}>
                <b>Scan to verify this certificate.</b> Confirms the §6418 registration number and the SHA‑256 e‑sign hash above. Retain for your records and claim the transferred credit on your federal income tax return (Form 3800) with the §6418 Transfer Election Statement. Not a check or negotiable instrument.
              </div>
            </div>
          </div>

          {/* §6418 TRANSFER & ASSIGNMENT + DESIGNATION OF PAYEE */}
          <div style={{ border: "1px solid #cfe0d7", borderRadius: 6, padding: "12px 16px", background: "#f6faf7", fontSize: "8.4pt", marginTop: 10, color: "#14204a", lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, color: "#0B3D2E", textTransform: "uppercase", letterSpacing: ".06em", fontSize: "8.5pt", borderBottom: "1px solid #cfe0d7", paddingBottom: 3, marginBottom: 6 }}>§6418 Transfer &amp; Assignment · Designation of Payee</div>

            <div style={{ fontWeight: 700, marginBottom: 2 }}>1. One‑Time Transfer of the Eligible Credit (§6418)</div>
            <p style={{ margin: "0 0 6px" }}>
              Pursuant to IRC §6418 and the SEUTRADE Master Solar Tax Credit Transfer Agreement, <b>{FIRM.name}</b> (the eligible taxpayer / Transferor) has transferred the §48 solar Investment Tax Credit described above to <b>{dash(borrowerName)}</b> (the Transferee, TIN {g("tin") ? maskTin(g("tin")) : "—"}) for cash. This is the single transfer permitted for this credit.
            </p>
            <p style={{ margin: "0 0 8px", fontSize: "7.6pt", color: "#7a2b1d", background: "#fdeee9", border: "1px solid #f3c9bd", borderRadius: 4, padding: "5px 8px" }}>
              <b>No second transfer.</b> Under IRC §6418(e)(2), the Transferee <b>may not re‑transfer or re‑assign the tax credit itself</b> to any other person. The credit is claimed only on the Transferee's own federal income tax return (Form 3800).
            </p>

            <div style={{ fontWeight: 700, marginBottom: 2 }}>2. Assignment of Cash Proceeds / Designated Payee (Permitted)</div>
            <p style={{ margin: "0 0 6px" }}>
              The Transferee <u>may</u> assign and direct the <b>cash proceeds or economic benefit</b> of this credit to a third party. This assigns money only — it does <b>not</b> transfer the tax credit and does not constitute a second §6418 transfer. The Transferee hereby designates the following third‑party payee to receive such proceeds:
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 18px", margin: "2px 0 8px" }}>
              <div><span style={{ fontSize: "7pt", textTransform: "uppercase", color: "#3c5a4c" }}>Designated Payee / Assignee</span><div style={{ borderBottom: "1px solid #0B3D2E", minHeight: "0.22in" }}>{g("assigneeName")}</div></div>
              <div><span style={{ fontSize: "7pt", textTransform: "uppercase", color: "#3c5a4c" }}>Payee TIN / EIN</span><div style={{ borderBottom: "1px solid #0B3D2E", minHeight: "0.22in" }}>{g("assigneeTin") ? maskTin(g("assigneeTin")) : ""}</div></div>
              <div style={{ gridColumn: "1 / span 2" }}><span style={{ fontSize: "7pt", textTransform: "uppercase", color: "#3c5a4c" }}>Payee Address</span><div style={{ borderBottom: "1px solid #0B3D2E", minHeight: "0.22in" }}>{g("assigneeAddress")}</div></div>
            </div>

            <div style={{ display: "flex", gap: 22, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ borderBottom: "1px solid #0B3D2E", minHeight: "0.3in", display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 2 }}>{bSig ? <span className="tc-sig-cursive" style={{ fontSize: "16pt" }}>{bSig.startsWith("/s/") ? bSig : `/s/ ${bSig}`}</span> : ""}</div>
                <div style={{ fontSize: "7pt", color: "#3c5a4c", marginTop: 2 }}>Transferee — {dash(borrowerName)} · Date {g("date") ? fmtDate(g("date")) : "____________"}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ borderBottom: "1px solid #0B3D2E", minHeight: "0.3in", display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 2 }}><span className="tc-sig-cursive" style={{ fontSize: "16pt" }}>{firmSig}</span></div>
                <div style={{ fontSize: "7pt", color: "#3c5a4c", marginTop: 2 }}>{FIRM.name} — {FIRM.officer}, Authorized Officer</div>
              </div>
            </div>
            <div style={{ fontSize: "6.6pt", color: "#5a7568", marginTop: 6, wordBreak: "break-all" }}><b>E‑Sign Hash (SHA‑256):</b> <span style={{ fontFamily: "'Courier New',monospace" }}>{execHash}</span> · Executed {stampTime}</div>
          </div>
        </div>
      </div>
    );
  }

  if (formKey === "instructions") {
    const steps = [
      ["Enrollment", `You enrolled in the ${FIRM.name} Tax Debt Assistance Program and authorized a one-time program fee plus a $9.99/month service fee. A secure client file was opened for you.`],
      ["IRS forms prepared & filed", "We auto-populate and file your selected official IRS request forms (e.g., Forms 2848, 8821, 12277, 911, 9465, 433-F, 843, 656) by secure eFax to the appropriate IRS unit to address any lien, levy or balance."],
      ["Solar tax credit transferred to you", `Under IRC §6418, ${FIRM.name} transfers you an eligible §48 solar Investment Tax Credit for cash. You receive a NON-NEGOTIABLE Solar Tax Credit Transfer Certificate evidencing the one-time transfer, plus the §6418 Transfer Election Statement.`],
      ["You claim the credit on your tax return", "Attach the §6418 Transfer Election Statement (with the IRS registration number) to your federal income tax return and claim the credit on Form 3800 (General Business Credit). The credit reduces your federal income-tax liability for the eligible year."],
      ["Keep your records & verify", "Retain your certificate. Scan the QR code on the certificate stub any time to verify its registration number and SHA-256 e-sign hash. Your monthly service fee keeps your representation active; you can cancel from your client portal."],
    ];
    return (
      <div className="tc-sheet tc-solo" data-testid={`tc-solo-instructions-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <div className="tc-head">
          <div className="tc-eyebrow">{FIRM.name} · Client Guide</div>
          <div className="tc-title" style={{ fontSize: "15pt" }}>How Your Program Works</div>
          <div className="tc-sub">A plain-language walkthrough of your Tax Debt Assistance &amp; Solar Tax Credit Transfer — keep this page with your package</div>
        </div>
        <div className="tc-callout" style={{ marginBottom: 10 }}>
          Prepared for <b>{dash(borrowerName)}</b>{g("tin") ? ` · TIN ${maskTin(g("tin"))}` : ""} · Certificate No. {creditNo} · IRS Registration No. {registrationNo}
        </div>
        {steps.map(([t, d], i) => (
          <div key={i} style={{ display: "flex", gap: 12, margin: "0 0 12px" }}>
            <div style={{ flex: "0 0 auto", width: 26, height: 26, borderRadius: "50%", background: "#0B3D2E", color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11pt" }}>{i + 1}</div>
            <div>
              <div style={{ fontWeight: 700, color: "#0B3D2E", fontSize: "11pt" }}>{t}</div>
              <p style={{ margin: "2px 0 0", fontSize: "9.5pt" }}>{d}</p>
            </div>
          </div>
        ))}
        <div className="tc-note" style={{ marginTop: 6 }}>
          No outcome is guaranteed. The tax credit and IRS filings are not issued, insured, or guaranteed by the United States, the Treasury, or the IRS, and are subject to IRS examination. Questions? Contact {FIRM.name}.
        </div>
      </div>
    );
  }

  if (formKey === "election") {
    return (
      <div className="tc-sheet tc-solo" data-testid={`tc-solo-election-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <div className="tc-head">
          <div className="tc-eyebrow">{FIRM.name} · IRC §6418</div>
          <div className="tc-title" style={{ fontSize: "14pt" }}>Transfer Election Statement</div>
          <div className="tc-sub">Attach to the transferee's federal income tax return · Registration No. {registrationNo}</div>
        </div>
        <ElectionStatement heading={false} />
      </div>
    );
  }

  if (TC_IRS_CODES.includes(formKey)) {
    const meta = TC_FORMS.find((f) => f.key === formKey) || {};
    return (
      <div className="tc-sheet tc-solo" data-testid={`tc-solo-${formKey}-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <div className="tc-head">
          <div className="tc-eyebrow">Official IRS Form</div>
          <div className="tc-title" style={{ fontSize: "15pt" }}>{meta.label}</div>
        </div>
        <div className="tc-callout">
          This is an <strong>official IRS fillable form</strong>. It renders from the government template (pre-filled with the client's
          identifying details where available) when you use <strong>Download PDF</strong> or <strong>eFax</strong> — it is not shown in this
          on-screen preview. Use the buttons above to generate it.
        </div>
      </div>
    );
  }

  if (formKey && blocks[formKey]) {
    return (
      <div className="tc-sheet tc-solo" data-testid={`tc-solo-${formKey}-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        {blocks[formKey]}
      </div>
    );
  }

  const agreementBlock = (
    <React.Fragment>
      <div className="tc-head">
        <div className="tc-eyebrow">Private Agreement · Authority: IRC §6418 (transfer of eligible credits) &amp; §48 (Investment Tax Credit)</div>
        <div className="tc-title">SEUTRADE Master Solar Tax Credit Transfer Agreement</div>
        <div className="tc-sub">Master terms for the one‑to‑one transfer of an eligible §48 solar Investment Tax Credit for cash under IRC §6418, with a per‑transaction Transfer Confirmation and the required Transfer Election Statement</div>
      </div>
      <F label="Agreement No. / Date">{agreementNo} · {g("date") ? fmtDate(g("date")) : <B />}</F>
      <F label="Authority">IRC §6418 (credit transfer) · IRC §48 (solar Investment Tax Credit)</F>
      <F label="Transferor / Seller">{FIRM.name} · EIN {FIRM.ein} · {FIRM.address}</F>
      <F label="Transferee / Client / Taxpayer">{dash(borrowerName)}</F>
      <F label="Transferee Address / TIN">{[dash(borrowerAddr), `TIN ${dash(g("tin"))}`].join(" · ")}</F>
      <div className="tc-art" style={{ marginTop: 8 }}>Transfer Confirmation — Specific Terms (governed by this Master Agreement)</div>
      <F label="Eligible Credit">{creditType}</F>
      <F label="Credit (Eligible) Taxable Year">{creditYear}</F>
      <F label="Amount of Credit Transferred">{debt ? money(debt) : <B />}</F>
      <F label="Cash Consideration (Purchase Price)">{money(fee)}</F>
      <F label="IRS Pre-Filing Registration Number">{registrationNo}</F>
      <F label="Credit Certificate No.">{creditNo}</F>

      <div className="tc-art">Article I — Authority &amp; Definitions</div>
      <p>This is a <strong>Master Agreement</strong> governing the one‑to‑one transfer of eligible credits from {FIRM.name} (the "Transferor") to the Transferee. Each specific transfer is documented by the <strong>Transfer Confirmation</strong> set out above and incorporated herein. It is made under <strong>IRC §6418</strong>, which permits an eligible taxpayer to elect to transfer all or a portion of an eligible credit to an unrelated transferee for cash, and <strong>IRC §48</strong>, which determines the Investment Tax Credit for qualifying solar energy property. The "Eligible Credit" is the §48 credit identified in the Transfer Confirmation, determined with respect to the Transferor's eligible credit property. This Agreement does not create, offer, or evidence any pooled fund, investment contract, promissory note, security, or interest in any enterprise; it is solely a direct purchase and transfer of a specified tax credit for cash.</p>

      <div className="tc-art">Article II — Transfer of the Eligible Credit</div>
      <p>The Transferor transfers to the Transferee the Eligible Credit in the amount of {debt ? money(debt) : "$____"} for the credit taxable year stated, for cash consideration. The transfer is a single, one-time transfer; the Transferee may not re-transfer the credit (§6418(b)(2)).</p>

      <div className="tc-art">Article III — Cash Consideration</div>
      <p>The Transferee shall pay the Transferor <strong>{money(fee)}</strong> in cash through the Program's secure Stripe checkout. Consistent with §6418(b), the consideration is not included in the Transferor's gross income and is not deductible by the Transferee.</p>

      <div className="tc-art">Article IV — IRS Pre-Filing Registration (Required)</div>
      <p>The transfer is valid only if the Transferor has completed IRS pre-filing registration for the credit property and furnished the resulting <strong>registration number</strong> ({registrationNo}) to the Transferee. Without a valid registration number the election is ineffective; the Transferor represents it will furnish the number and the minimum required substantiating documentation.</p>

      <div className="tc-art">Article V — Manner of Claiming the Credit</div>
      <p>The Transferee claims the transferred credit on its <strong>federal income tax return</strong> for the eligible year by filing <strong>Form 3800, General Business Credit</strong>, together with the <strong>Transfer Election Statement</strong> set forth below and the registration number. The credit reduces the Transferee's federal income-tax liability for that year; it does not, by itself, release a lien or levy or satisfy a separately assessed balance outside the return process.</p>

      <div className="tc-art">Article VI — Representations &amp; Warranties</div>
      <p>The Transferor represents that the Eligible Credit is valid, properly registered, and not previously transferred. The Transferee represents that it is an unrelated party with federal income-tax liability against which the credit may be applied and that it will claim the credit in accordance with law.</p>

      <div className="tc-art">Article VII — Recapture &amp; Indemnity</div>
      <p>The parties acknowledge that the credit may be subject to recapture under <strong>IRC §50</strong>. Recapture arising from the Transferee's acts or dispositions is borne by the Transferee; recapture arising from the Transferor's credit property or its ineligibility is borne by the Transferor, which shall indemnify the Transferee for the consideration paid in such event.</p>

      <div className="tc-art">Article VIII — Limitations &amp; No Guarantee</div>
      <p>Use of the transferred credit is subject to the general business credit ordering rules and, for individuals, the passive activity (§469) and at-risk (§465) limitations, which may limit or defer the benefit. The credit and the related filings are not issued, insured, or guaranteed by the United States, the Treasury, or the IRS, and are subject to IRS examination.</p>

      <div className="tc-art">Article IX — Related Representation Engagement</div>
      <p>The Transferee may engage the Firm to prepare and submit related IRS request forms (Forms 2848, 8821, 12277, 911, 9465, 433-F, 843, 656 and the 1040-V payment voucher) to address any existing lien, levy or balance. Those forms are the Transferee's own requests to the IRS for consideration and are governed by the accompanying Retainer and Limited Power of Attorney.</p>

      <div className="tc-art">Article X — Governing Law, Arbitration &amp; Execution</div>
      <F label="Governing State">{g("state")}</F>
      <p>Disputes shall be resolved by binding arbitration under the American Arbitration Association (AAA) rules. This Agreement is executed as of the date stated.</p>
      <div className="tc-sigrow">
        <div>
          <p style={{ fontWeight: 700 }}>Transferor / Seller</p>
          <p>Signature: <CursiveSig name={firmSig} /> — {FIRM.officer}, Authorized Officer</p>
          <p>For: {FIRM.name}  ·  Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
        <div>
          <p style={{ fontWeight: 700 }}>Transferee / Client / Taxpayer</p>
          <p>Signature: <CursiveSig name={bSig} /></p>
          <p>Name: {dash(borrowerName)}  ·  Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
        </div>
      </div>
      <p>Execution Date / Time: {stampTime} · Execution Hash (SHA-256):</p>
      <p><span className="tc-hash">{execHash}</span></p>
      <p style={{ fontSize: "9pt" }}>Subscribed and acknowledged before me this ______ day of __________, 20____ — Notary Public, State of {dash(g("state"))}. [Notary Seal]</p>

      <ElectionStatement heading={true} />
    </React.Fragment>
  );

  if (formKey === "agreement") {
    return (
      <div className="tc-sheet tc-solo" data-testid={`tc-solo-agreement-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        {agreementBlock}
      </div>
    );
  }

  return (
    <div className="tc-sheet" data-testid={`tc-doc-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
      {agreementBlock}
      {retainer}
      {poa}
    </div>
  );
}

const Ctl = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#0B3D2E]" />
  </div>
);

export default function TaxCreditTransfer() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const [formSel, setFormSel] = useState(() => q.get("form") || "");
  const activeForm = formSel || null;
  const activeLabel = (TC_FORMS.find((f) => f.key === formSel) || {}).label || "";
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [shared, setShared] = useState(tcDefaults);
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));

  const [catalog, setCatalog] = useState([]);
  const [selForms, setSelForms] = useState([]);
  const [formsMsg, setFormsMsg] = useState("");
  const [regCheck, setRegCheck] = useState(null);
  const [binderUrl, setBinderUrl] = useState("");
  const [binderBusy, setBinderBusy] = useState(false);
  const [binderErr, setBinderErr] = useState("");

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((x) => x.trim()).filter(Boolean) : (appId ? [appId] : []);
    (async () => {
      const results = [];
      for (const id of ids) {
        try { const r = await axios.get(`${API}/borrower/applications/${id}`, cfg); if (r.data.application) results.push(r.data.application); } catch { /* skip */ }
      }
      if (results[0]) {
        const savedSettings = (results.find((a) => a.tax_credit_settings) || {}).tax_credit_settings || null;
        if (savedSettings) setShared((p) => ({ ...p, ...savedSettings }));
        setSelForms(results[0].tax_forms_selected || TC_IRS_CODES.slice());
      }
      setApps(results);
      setLoading(false);
    })();
    try { axios.get(`${API}/admin/tax-credit/forms-catalog`, cfg).then((r) => setCatalog(r.data.forms || [])); } catch { /* */ }
    // eslint-disable-next-line
  }, [appId, idsParam]);

  const feeDisplay = apps[0] ? feeForDebt(Number(shared.taxDebt) || Number(apps[0].tax_debt || apps[0].payoff_amount || apps[0].current_balance) || 0) : 0;

  useEffect(() => {
    const v = (shared.registrationNo || "").trim();
    if (!v) { setRegCheck(null); return; }
    const t = setTimeout(async () => {
      try { const r = await axios.post(`${API}/admin/tax-credit/validate-registration`, { value: v }, cfg); setRegCheck(r.data); }
      catch { setRegCheck(null); }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [shared.registrationNo]);

  const toggleForm = (code) => setSelForms((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));

  const fetchBinder = async (body) => {
    if (!apps.length) return;
    setBinderBusy(true); setBinderErr("");
    try {
      const r = await axios.post(`${API}/admin/tax-credit/bulk-pdf`, { app_ids: apps.map((a) => a.id), ...body }, { ...cfg, responseType: "blob" });
      setBinderUrl((prev) => { if (prev) window.URL.revokeObjectURL(prev); return window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); });
    } catch (e) { setBinderErr(e.response?.data?.detail || "Could not render the form binder."); setBinderUrl(""); }
    finally { setBinderBusy(false); }
  };
  const previewFormsBinder = () => fetchBinder({ forms: selForms });
  const closeBinder = () => setBinderUrl((prev) => { if (prev) window.URL.revokeObjectURL(prev); return ""; });

  // When an official IRS form is chosen in the dropdown, show the whole form inline.
  useEffect(() => {
    if (!apps.length) return;
    if (TC_IRS_CODES.includes(activeForm)) fetchBinder({ form: activeForm });
    else closeBinder();
    // eslint-disable-next-line
  }, [formSel, apps.length]);

  const saveForms = async () => {
    if (!apps.length) return;
    setFormsMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/tax-credit/${a.id}/forms`, { codes: selForms }, cfg)));
      setFormsMsg("Form selection saved ✓"); setTimeout(() => setFormsMsg(""), 4000);
    } catch { setFormsMsg("Could not save selection"); setTimeout(() => setFormsMsg(""), 4000); }
  };

  const saveDocData = async () => {
    if (!apps.length) return;
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { tax_credit_settings: shared }, cfg)));
      setSavedMsg("Saved to file ✓"); setTimeout(() => setSavedMsg(""), 4000);
    } catch { setSavedMsg("Save failed"); setTimeout(() => setSavedMsg(""), 4000); }
    finally { setSaving(false); }
  };

  const [dlBusy, setDlBusy] = useState(false);
  const downloadPdf = async () => {
    if (!apps.length) return;
    setDlBusy(true);
    try {
      const r = await axios.post(`${API}/admin/tax-credit/bulk-pdf`, { app_ids: apps.map((a) => a.id), form: activeForm }, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const base = activeForm === "election" ? "sec6418_election_statement" : (activeForm ? `tax_${activeForm}` : "tax_resolution_package");
      a.href = url; a.download = apps.length > 1 ? `${base}_${apps.length}.pdf` : `${base}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || "Could not build the merged PDF."); }
    finally { setDlBusy(false); }
  };

  const [faxOpen, setFaxOpen] = useState(false);
  const [faxTo, setFaxTo] = useState("+18553903530");
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxMsg, setFaxMsg] = useState(null);
  const sendFax = async () => {
    if (!apps.length || !faxTo.trim()) return;
    setFaxBusy(true); setFaxMsg(null);
    try {
      const r = await axios.post(`${API}/admin/tax-credit/efax`, { app_ids: apps.map((a) => a.id), to: faxTo.trim(), form: activeForm, label: `${activeForm ? activeLabel : "Tax Resolution Package"} · ${apps.length} client(s)` }, cfg);
      const fid = r.data?.fax?.fax_id || r.data?.fax?.id || "";
      const st = r.data?.fax?.status || "queued";
      setFaxMsg({ ok: true, text: `✓ Fax submitted to ${faxTo.trim()} — ${apps.length} client(s). Telnyx ID: ${fid || "n/a"} · Status: ${st}` });
    } catch (e) { setFaxMsg({ ok: false, text: e.response?.data?.detail || "Fax failed to send." }); }
    finally { setFaxBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, color: "#ECECE4", fontFamily: "serif" }}>Loading…</div>;

  return (
    <div className="tc-workbench" style={{ background: "#0B0B07", minHeight: "100vh" }}>
      <div className="no-print" style={{ background: "#06160F", borderBottom: "1px solid #0B3D2E", padding: "12px 18px" }}>
        <div style={{ maxWidth: "8.5in", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <a href="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#6FD3AE", fontSize: 12 }}><ArrowLeft size={14} /> Back</a>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6FD3AE", fontWeight: 700, letterSpacing: ".06em", fontSize: 12 }}>
            <Landmark size={16} /> {activeForm ? (TC_FORMS.find((f) => f.key === formSel) || {}).short?.toUpperCase() : "TAX CREDIT TRANSFER"}{apps.length > 1 ? ` · ${apps.length} CLIENTS` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select value={formSel} onChange={(e) => setFormSel(e.target.value)} data-testid="tc-form-select"
              style={{ background: "#0F0F0A", color: "#ECECE4", border: "1px solid #0B3D2E", borderRadius: 6, padding: "9px 10px", fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", maxWidth: 240 }}>
              {TC_FORMS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="tc-save" style={btn("#6FD3AE", "#0B3D2E")}><Save size={13} /> {saving ? "Saving…" : "Save to File"}</button>
            <button onClick={downloadPdf} disabled={dlBusy || !apps.length} data-testid="tc-download" style={btn("#6FD3AE", "#0B3D2E")}><Download size={13} /> {dlBusy ? "Building…" : "Download PDF"}</button>
            <button onClick={() => setFaxOpen((x) => !x)} disabled={!apps.length} data-testid="tc-efax-toggle" style={btn("#6FD3AE", "#0B3D2E")}><Send size={13} /> eFax IRS</button>
            <button onClick={() => window.print()} data-testid="tc-print" style={{ ...btn("#0B0B07", "#B8860B"), background: "#E4C97A", color: "#0B0B07" }}><Printer size={13} /> Print / Save as PDF</button>
          </div>
        </div>
        {savedMsg && <div className="text-[12px] text-[#5FE0B0] mt-2" data-testid="tc-saved-msg" style={{ maxWidth: "8.5in", margin: "6px auto 0" }}>{savedMsg}</div>}

        <div style={{ maxWidth: "8.5in", margin: "10px auto 0" }} className="rounded-md border border-[#0B3D2E]/50 bg-[#0A140F] p-3" data-testid="tc-forms-panel">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#6FD3AE] mb-2 flex items-center gap-2">
            <FileCheck /> IRS forms to include (select all that apply)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 6 }}>
            {catalog.map((f) => (
              <label key={f.code} data-testid={`tc-formchk-${f.code}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#C8DAD2", cursor: "pointer", padding: "4px 6px", borderRadius: 6, background: selForms.includes(f.code) ? "#07251a" : "transparent", border: `1px solid ${selForms.includes(f.code) ? "#14503B" : "#1b2b23"}` }}>
                <input type="checkbox" checked={selForms.includes(f.code)} onChange={() => toggleForm(f.code)} />
                <span>Form {f.no}{f.prefilled ? " · auto-filled" : ""}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
            <button onClick={saveForms} disabled={!apps.length} data-testid="tc-forms-save" style={btn("#6FD3AE", "#0B3D2E")}><Save size={12} /> Save selection</button>
            <button onClick={previewFormsBinder} disabled={!apps.length || binderBusy} data-testid="tc-forms-preview" style={{ ...btn("#0B0B07", "#6FD3AE"), background: "#6FD3AE", color: "#0B0B07" }}><FileText size={12} /> {binderBusy ? "Building binder…" : "Preview forms binder"}</button>
            {formsMsg && <span className="text-[12px] text-[#5FE0B0]" data-testid="tc-forms-msg">{formsMsg}</span>}
            {binderErr && <span className="text-[12px] text-[#FF8585]" data-testid="tc-binder-err">{binderErr}</span>}
          </div>
        </div>

        {faxOpen && (
          <div style={{ maxWidth: "8.5in", margin: "10px auto 0" }} className="rounded-md border border-[#0B3D2E]/50 bg-[#0A140F] p-3" data-testid="tc-efax-panel">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#6FD3AE] mb-2 flex items-center gap-2"><Send size={12} /> eFax to IRS — {activeForm ? activeLabel : "Full Package"}{apps.length > 1 ? ` (all ${apps.length} merged)` : ""}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 240 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">IRS destination</label>
                <select onChange={(e) => e.target.value && setFaxTo(e.target.value)} data-testid="tc-efax-dir" className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4]">
                  <option value="+18553903530">IRS — CLO Lien Release/Withdrawal · 855-390-3530</option>
                  <option value="+18557964524">IRS — ACS Levy/Garnishment Release · 855-796-4524</option>
                  <option value="+18557538177">IRS — Centralized Lien Operation · 855-753-8177</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Destination fax number</label>
                <input value={faxTo} onChange={(e) => setFaxTo(e.target.value)} data-testid="tc-efax-to"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#0B3D2E]" />
              </div>
              <button onClick={sendFax} disabled={faxBusy || !faxTo.trim()} data-testid="tc-efax-send" style={{ ...btn("#0B0B07", "#0B3D2E"), background: "#6FD3AE", color: "#0B0B07" }}><Send size={13} /> {faxBusy ? "Sending…" : "Send eFax"}</button>
            </div>
            {faxMsg && <div data-testid="tc-efax-msg" className={`text-[12px] font-bold mt-2 ${faxMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{faxMsg.text}</div>}
          </div>
        )}

        <div style={{ maxWidth: "8.5in", margin: "12px auto 0" }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Ctl label="Agreement No." value={shared.agreementNo} onChange={set("agreementNo")} />
          <Ctl label="IRS Registration No." value={shared.registrationNo} onChange={set("registrationNo")} />
          <Ctl label="Credit Cert. No." value={shared.certNo} onChange={set("certNo")} />
          <Ctl label="Date" value={shared.date} onChange={set("date")} type="date" />
          <Ctl label="Eligible Credit Type" value={shared.creditType} onChange={set("creditType")} />
          <Ctl label="Credit (Eligible) Year" value={shared.creditYear} onChange={set("creditYear")} />
          <Ctl label="Credit Amount / IRS Balance ($)" value={shared.taxDebt} onChange={set("taxDebt")} type="number" />
          <Ctl label="TIN / SSN" value={shared.tin} onChange={set("tin")} />
          <Ctl label="Tax Period(s)" value={shared.taxYears} onChange={set("taxYears")} />
          <Ctl label="NFTL Serial / Lien No." value={shared.lienSerial} onChange={set("lienSerial")} />
          <Ctl label="Notice / Levy Reference" value={shared.noticeFileRef} onChange={set("noticeFileRef")} />
          <Ctl label="IRS Office / Address" value={shared.irsOffice} onChange={set("irsOffice")} />
          <Ctl label="Governing State" value={shared.state} onChange={set("state")} />
          <Ctl label="Employer / Garnishee Name" value={shared.employerName} onChange={set("employerName")} />
          <Ctl label="Bank / Financial Institution" value={shared.bankName} onChange={set("bankName")} />
          <Ctl label="Bank Account No. (last 4 shown)" value={shared.bankAccount} onChange={set("bankAccount")} />
          <Ctl label="Designated Payee / Assignee (cash proceeds)" value={shared.assigneeName} onChange={set("assigneeName")} />
          <Ctl label="Payee TIN / EIN (last 4 shown)" value={shared.assigneeTin} onChange={set("assigneeTin")} />
          <Ctl label="Payee Address" value={shared.assigneeAddress} onChange={set("assigneeAddress")} />
        </div>
        <div className="text-[11px] text-[#6FD3AE] mt-2 font-bold" style={{ maxWidth: "8.5in", margin: "8px auto 0" }} data-testid="tc-fee-badge">
          Client fee: {money(feeDisplay)} + $9.99/mo &nbsp;<span className="text-[#8A8A8A] font-normal">($850 base for the first $10,000, +$25 per additional $1,000; recurring $9.99 monthly service fee)</span>
        </div>
        {regCheck && (
          <div data-testid="tc-reg-badge" style={{ maxWidth: "8.5in", margin: "6px auto 0", fontSize: 11.5, fontWeight: 700, color: regCheck.valid ? "#6FD3AE" : "#E4C97A" }}>
            IRS §6418 registration no.: {regCheck.valid ? `✓ valid (${regCheck.normalized})` : `⚠ ${regCheck.reason}`}
          </div>
        )}
        <div className="text-[10px] text-[#8A8A8A] mt-1" style={{ maxWidth: "8.5in", margin: "4px auto 0" }}>{FIRM.name} transfers an eligible §48 solar Investment Tax Credit to the client under IRC §6418; the client claims it on their federal income tax return (Form 3800 + Transfer Election Statement). IRS Forms 2848/8821/12277/911/9465/433-F/843/656/1040-V are the official government templates. Nothing here is issued or guaranteed by the IRS.</div>
      </div>

      <div className="cm-print-area" style={{ padding: "24px 0" }}>
        {!apps.length
          ? <div style={{ color: "#9A9A90", textAlign: "center", padding: 40 }}>No application loaded. Open this from a borrower file, or pass ?ids=… for bulk.</div>
          : binderUrl
            ? (
              <div style={{ maxWidth: "8.5in", margin: "0 auto" }} data-testid="tc-binder">
                <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ color: "#6FD3AE", fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase" }}>
                    {TC_IRS_CODES.includes(activeForm) ? `Official IRS form — ${activeLabel}` : `Official IRS forms binder (${selForms.length} selected)`}{apps.length > 1 ? ` · ${apps.length} clients` : ""}
                  </span>
                  <button onClick={closeBinder} data-testid="tc-binder-close" style={btn("#6FD3AE", "#0B3D2E")}>Back to editable preview</button>
                </div>
                <iframe title="IRS forms binder" src={`${binderUrl}#view=FitH`} data-testid="tc-binder-frame"
                  style={{ width: "100%", height: "1150px", border: "1px solid #14503B", borderRadius: 6, background: "#fff" }} />
              </div>
            )
            : apps.map((a, i) => <TaxCreditTransferDoc key={a.id} app={a} shared={shared} last={i === apps.length - 1} formKey={activeForm} />)}
      </div>
      <style>{TC_CSS}</style>
    </div>
  );
}

const FileCheck = () => <Save size={12} />;
const btn = (color, border) => ({ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color, border: `1px solid ${border}`, padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" });
